'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { Payment } from '@/core/domain';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import type { GetPaymentsByUserIdUseCase } from '../../domain/usecases/get-payments-by-user-id';
import type { SubscribePaymentDocForUserUseCase } from '../../domain/usecases/subscribe-payment-doc-for-user';

export interface BillingInvoicesState {
  payments: Payment[];
  loading: boolean;
  error: string | null;
}

export interface RefreshPaymentsOptions {
  forceRefresh?: boolean;
}

export interface BillingInvoicesEvents {
  /** Carrega faturas do usuário autenticado. Na montagem usa `forceRefresh` para evitar lista obsoleta no cache. */
  refresh: (options?: RefreshPaymentsOptions) => Promise<void>;
  clearError: () => void;
  /**
   * Escuta `payments/{paymentId}` para o usuário autenticado (validação no datasource).
   * @return Função para cancelar a inscrição.
   */
  subscribePaymentDoc: (
    paymentId: string,
    onNext: (payment: Payment | null) => void,
    onError: (error: Error) => void
  ) => () => void;
}

export interface BillingInvoicesContextValue {
  state: BillingInvoicesState;
  events: BillingInvoicesEvents;
}

export interface BillingInvoicesProviderProps {
  children: React.ReactNode;
  getPaymentsByUserIdUseCase: GetPaymentsByUserIdUseCase;
  subscribePaymentDocForUserUseCase: SubscribePaymentDocForUserUseCase;
}

const initialState: BillingInvoicesState = {
  payments: [],
  loading: false,
  error: null,
};

const BillingInvoicesContext = createContext<BillingInvoicesContextValue | null>(null);

export function BillingInvoicesProvider({
  children,
  getPaymentsByUserIdUseCase,
  subscribePaymentDocForUserUseCase,
}: BillingInvoicesProviderProps) {
  const { state: authState } = useAuth();
  const { state: userState } = useUser();
  /** Doc `users` pode atrasar após redirect do Stripe; Firebase `uid` bate com `payments.user_id`. */
  const userId = userState.currentUser?.id ?? authState.user?.uid ?? undefined;
  const [state, setState] = useState<BillingInvoicesState>(initialState);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const refresh = useCallback(
    async (options?: RefreshPaymentsOptions) => {
      if (!userId) {
        setState({ payments: [], loading: false, error: null });
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const payments = await getPaymentsByUserIdUseCase.execute(userId, {
          forceRefresh: options?.forceRefresh === true,
        });
        setState({ payments, loading: false, error: null });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, loading: false, error: msg }));
      }
    },
    [getPaymentsByUserIdUseCase, userId],
  );

  useEffect(() => {
    void refresh({ forceRefresh: true });
  }, [refresh]);

  const subscribePaymentDoc = useCallback(
    (
      paymentId: string,
      onNext: (payment: Payment | null) => void,
      onError: (error: Error) => void
    ) => {
      if (!userId) {
        onError(new Error('Usuário não autenticado.'));
        return () => {};
      }
      return subscribePaymentDocForUserUseCase.execute(paymentId, userId, onNext, onError);
    },
    [subscribePaymentDocForUserUseCase, userId]
  );

  const events: BillingInvoicesEvents = useMemo(
    () => ({ refresh, clearError, subscribePaymentDoc }),
    [refresh, clearError, subscribePaymentDoc]
  );

  const value = useMemo(() => ({ state, events }), [state, events]);

  return (
    <BillingInvoicesContext.Provider value={value}>
      {children}
    </BillingInvoicesContext.Provider>
  );
}

export function useBillingInvoices(): BillingInvoicesContextValue {
  const ctx = useContext(BillingInvoicesContext);
  if (!ctx) {
    throw new Error('useBillingInvoices must be used within BillingInvoicesProvider');
  }
  return ctx;
}
